<script setup lang="ts">
import asciiFoldingFilter from '@sancare/typescript-commons/src/text-processing/asciiFoldingFilter'
import Fuse from 'fuse.js'
import _ from 'lodash'
import { computed, ref, watch } from 'vue'

import { ListedSavedSearch, SearchGroup } from '@/saved-searches/types'
import { savedSearchLabels } from '@/saved-searches/utils'
import { ModeEnum } from '@/store/modules/settings/types'

const props = defineProps<{
  mode: ModeEnum
  privateSearches: ListedSavedSearch[]
  teamSearches: { title: string, searches: ListedSavedSearch[] }[]
  publicCategories: { id: number, name: string, searches: ListedSavedSearch[] }[]
  userSearchGroups: SearchGroup[]
  sancareCategories: { id: number, name: string, countTodoStay:number, searches: ListedSavedSearch[] }[]
  recentlyUsedSearches: ListedSavedSearch[]
}>()

const emit = defineEmits<{
  'quick-filter-disable': []
  'quick-filter-update': [results: {
    privateSearches: number[]
    teamSearches: number[]
    publicCategories: { [categoryId: number]: number[] }
    sancareCategories: { [categoryId: number]: number[] }
    userSearchGroups: number[]
    recentlyUsedSearches: number[]
  }]
}>()

const typedText = ref('')

const searchablePrivateSearches = computed(() => new Fuse(
  props.privateSearches.map((s) => ({ id: s.id, name: asciiFoldingFilter(s.name), reference: s.reference })),
  { keys: ['name', 'reference'], shouldSort: false, threshold: 0.3 },
))

const searchableTeamSearches = computed(() => {
  const flatSearches = _.flatten(props.teamSearches.map((item) => item.searches))

  return new Fuse(
    flatSearches.map((s) => ({ id: s.id, name: asciiFoldingFilter(s.name), reference: s.reference })),
    { keys: ['name', 'reference'], shouldSort: false, threshold: 0.3 },
  )
})

const searchablePublicSearches = computed(() => {
  const flatSearches = _.flatten(props.publicCategories.map((c) => c.searches.map((s) => ({
    categoryId: c.id,
    searchId: s.id,
    categoryName: asciiFoldingFilter(c.name),
    searchName: asciiFoldingFilter(s.name),
    reference: s.reference,
  }))))

  return new Fuse(
    flatSearches,
    { keys: ['categoryName', 'searchName', 'reference'], shouldSort: false, threshold: 0.3 },
  )
})

const searchableUserSearchGroups = computed(() => new Fuse(
  props.userSearchGroups.map((s) => ({ id: s.id, name: asciiFoldingFilter(s.name) })),
  { keys: ['name'], shouldSort: false, threshold: 0.3 },
))

const searchableRecentlyUsedSearches = computed(() => new Fuse(
  props.recentlyUsedSearches.map((s) => ({ id: s.id, name: asciiFoldingFilter(s.name), reference: s.reference })),
  { keys: ['name', 'reference'], shouldSort: false, threshold: 0.3 },
))

const searchableSancareSearches = computed(() => {
  const flatSearches = props.sancareCategories.flatMap((category) => category.searches.map((search) => ({
    categoryId: category.id,
    searchId: search.id,
    categoryName: asciiFoldingFilter(category.name),
    searchName: asciiFoldingFilter(search.name),
    reference: search.reference,
  })))

  return new Fuse(
    flatSearches,
    { keys: ['categoryName', 'searchName', 'reference'], shouldSort: false, threshold: 0.3 },
  )
})

const placeholder = computed(() => `Nom de la ${savedSearchLabels(props.mode).singular}, de la catégorie...`)

watch(typedText, _.debounce((newTypedText: string) => {
  const searchPattern = asciiFoldingFilter(newTypedText.trim())
  if (!searchPattern) {
    emit('quick-filter-disable')

    return
  }

  emit('quick-filter-update', {
    privateSearches: searchablePrivateSearches.value.search(searchPattern).map((search) => search.item.id),
    teamSearches: searchableTeamSearches.value.search(searchPattern).map((search) => search.item.id),
    publicCategories: _.mapValues( // use of _.mapValues allow using groupBy map as an array
      _.groupBy(searchablePublicSearches.value.search(searchPattern), 'item.categoryId'),
      (searches) => searches.map((search) => search.item.searchId)
    ),
    userSearchGroups: searchableUserSearchGroups.value.search(searchPattern).map((group) => group.item.id),
    recentlyUsedSearches: searchableRecentlyUsedSearches.value.search(searchPattern).map((search) => search.item.id),
    sancareCategories: _.mapValues( // use of _.mapValues allow using groupBy map as an array
      _.groupBy(searchableSancareSearches.value.search(searchPattern), 'item.categoryId'),
      (searches) => searches.map((search) => search.item.searchId)
    )
  })
}, 500, { trailing: true, maxWait: 2000 }))
</script>

<template>
  <div class="home-list-part">
    <input
      id="homepage-searches-quick-filter"
      v-model="typedText"
      :placeholder="placeholder"
      type="text"
      class="form-control mx-2"
    >
  </div>
</template>
